// Phase DP polish 2 — render the rental.deposit field for the
// EQUIPMENT card's "Deposit" row.
//
// The deposit column has two historical shapes:
//   * Phase DP-R3 (current) — a subdocument:
//       { status, amount_cents, payment_method, ..., history: [] }
//     so ``${"$" + rental.deposit}`` stringifies to "$[object Object]".
//   * Legacy hotfix era — a flat number (dollars), e.g. ``1900``.
//
// This helper coerces either shape (plus null/undefined and weird
// edge cases like ``{}`` with no ``amount_cents`` key) into a clean
// display string. Returns ``"—"`` (em dash) for "no value", which
// matches the other empty cells in the EQUIPMENT card.
//
// Output examples:
//   formatDepositAmount(null)                              → "—"
//   formatDepositAmount(undefined)                         → "—"
//   formatDepositAmount({})                                → "—"
//   formatDepositAmount({ amount_cents: 190000 })          → "$1,900.00"
//   formatDepositAmount({ amount_cents: 50000 })           → "$500.00"
//   formatDepositAmount({ amount_cents: 0,
//                         status: "not_collected" })       → "—"
//   formatDepositAmount(1900)            // legacy float   → "$1900"
//   formatDepositAmount("1900")          // legacy string  → "$1900"
export function formatDepositAmount(deposit) {
    if (deposit == null) return "—";
    // Legacy: a bare number or numeric-looking string.
    if (typeof deposit === "number") {
        return Number.isFinite(deposit) ? `$${deposit}` : "—";
    }
    if (typeof deposit === "string") {
        const trimmed = deposit.trim();
        if (!trimmed) return "—";
        // Strip a leading "$" if the legacy writer included one.
        const normalised = trimmed.startsWith("$") ? trimmed.slice(1) : trimmed;
        const n = Number(normalised);
        if (!Number.isNaN(n)) return `$${normalised}`;
        return "—";
    }
    // DP-R3 object shape.
    if (typeof deposit === "object") {
        const cents = deposit.amount_cents;
        if (typeof cents !== "number" || !Number.isFinite(cents) || cents <= 0) {
            return "—";
        }
        return (cents / 100).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
        });
    }
    return "—";
}

// Phase DP polish 3 — extract the dollar amount of a deposit field as a
// **plain numeric string** suitable for `<input type="number">` (or for
// PATCHing back to the rental endpoint, which expects dollars).
//
// Why: ``AdminContractDetailPage`` initialises its rental edit form from
// the rental row directly. Pre-DP-R3 ``rental.deposit`` was a number, so
// ``deposit: rt.data.deposit ?? ""`` worked. Post-DP-R3 the field is a
// dict, which React serialises into a number input as ``"[object Object]"``.
//
// Output examples (always a STRING, never a dict):
//   depositToAmountInput(null)                              → ""
//   depositToAmountInput({})                                → ""
//   depositToAmountInput({ amount_cents: 190000 })          → "1900"
//   depositToAmountInput({ amount_cents: 75050 })           → "750.5"
//   depositToAmountInput(1900)            // legacy number  → "1900"
//   depositToAmountInput("1900")          // legacy string  → "1900"
//   depositToAmountInput("$1,900.00")     // legacy display → "1900.00"
export function depositToAmountInput(deposit) {
    if (deposit == null) return "";
    if (typeof deposit === "number") {
        return Number.isFinite(deposit) ? String(deposit) : "";
    }
    if (typeof deposit === "string") {
        const trimmed = deposit.trim();
        if (!trimmed) return "";
        // Strip "$" and commas a legacy writer may have included.
        const normalised = trimmed.replace(/^\$/, "").replace(/,/g, "");
        return Number.isNaN(Number(normalised)) ? "" : normalised;
    }
    if (typeof deposit === "object") {
        const cents = deposit.amount_cents;
        if (typeof cents !== "number" || !Number.isFinite(cents)) return "";
        // Use a string so trailing-zero precision doesn't get stripped
        // unexpectedly by toFixed(2). Drop trailing zeros for cleanliness.
        const dollars = cents / 100;
        // ``Number.prototype.toString`` already drops trailing zeros.
        return String(dollars);
    }
    return "";
}
